import { Fragment, useEffect, useMemo, useRef } from 'react'

import { getCardIsDownsell } from '@mediaingenuity/totallymoney.cardloanutils'
import { ScoredCreditCardProduct } from '@mediaingenuity/totallymoney.types'
import { captureException } from '@sentry/nextjs'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import Box from '@ilg-totallymoney/ui/components/Box'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import Grid from '@ilg-totallymoney/ui/components/Grid'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'
import LinkWithChevron from '@ilg-totallymoney/ui/components/LinkWithChevron'
import Text from '@ilg-totallymoney/ui/components/Text'
import theme from '@ilg-totallymoney/ui/theme'

import { GuaranteedOfferType } from '@appTypes/App.interface'
import {
    ApplicationURLTriggerLocation,
    CreditCardProductCategory,
    GetMinimumGuaranteedCreditLimitResponse,
    ScoredCreditCardProductsCategory,
    ScoredExperiment,
} from '@appTypes/EligibilityApi.interface'

import AppAdoptionHeader from '@components/AppAdoptionHeader'
import AppAdoptionModal from '@components/AppAdoptionModal'
import BrokerInfo from '@components/BrokerInfo'
import CardTablePagination from '@components/CardTablePagination'
import CategoryFilterModal from '@components/CategoryFilterModal'
import ErrorBoundary from '@components/ErrorBoundary'
import GuaranteedOfferModal from '@components/GuaranteedOfferModal'
import MinimumCreditLimitModal from '@components/MinimumCreditLimitModal/MinimumCreditLimitModal'
import NextLinkButton from '@components/NextLinkButton'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'
import SortByModal from '@components/SortByModal'

import { categories } from '@config/categories'

import useAppQueryState from '@hooks/useAppQueryState'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'
import usePostCreditCardsEmailTrigger from '@hooks/usePostCreditCardsEmailTrigger'
import useSetState from '@hooks/useSetState'

import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'

import useGetCreditCardApplicationUrl from '@routes/ScoredTableRoute/hooks/useGetCardApplicationUrl'
import useGetDifferenceInNewCreditCardsOffers from '@routes/ScoredTableRoute/hooks/useGetDifferenceInNewCreditCardsOffers'
import {
    getIsSponsoredCreditCard,
    getSponsoredProducts,
} from '@routes/ScoredTableRoute/utils/getSponsoredProducts'

import {
    buildAcquisitionJourneyFormAcceptEventPayload,
    buildAcquisitionJourneyScoredTableClickoutEventPayload,
} from '@utils/buildAcquisitionEvents'
import { getAppConfig } from '@utils/getAppConfig'
import getCardPosition from '@utils/getCardPosition'
import getCategoryByCategoryId from '@utils/getCategoryByCategoryId'
import { getCategoryPathForId } from '@utils/getCategoryPathForId'
import { getClickId } from '@utils/getClickId'
import getGuaranteedOfferType from '@utils/getGuaranteedOfferType'
import { getSanitisedCardName } from '@utils/getSanitisedCardName'
import { getShowOfferDurationContent } from '@utils/getShowOfferDurationContent'
import { sortCardsBySortKey } from '@utils/sortCardsBySortKey'

import ScoredTableFaqs from '../ScoredTableFaqs'
import AdvertisementBanner from './components/AdvertisementBanner'
import DownsellBanner from './components/DownsellBanner'
import HighlightHero from './components/HighlightHero'
import ScoredTableHeader from './components/ScoredTableHeader'
import ScoredTableItem from './components/ScoredTableItem'
import ScoredTableNewOfferCard from './components/ScoredTableNewOfferCard'
import TotallySureTooltipBanner from './components/TotallySureTooltipBanner'
import useLogCheckMatchFactorRankingError from './hooks/useLogCheckMatchFactorRankingError'
import {
    CategoryComparatorKey,
    CategorySortKeyComparatorKeys,
    categorySortKeyComparatorKeys,
} from './utils/categorySortKeyComparatorKeys'
import { getCategorySortKeysToShow } from './utils/getCategorySortKeysToShow'
import { getDownsellCreditCards } from './utils/getDownsellCreditCards'
import { HighlightCard, HighlightCardStatus } from './utils/getHighlightCard'
import { getIsBestMatchCard } from './utils/getIsBestMatchCard'
import { getValidCategorySortKey } from './utils/getValidCategorySortKey'

enum ModalType {
    AppAdoption = 'AppAdoption',
    BestMatch = 'BestMatch',
    CategoryFilter = 'CategoryFilter',
    CreditLimitModal = 'CreditLimitModal',
    GuaranteedOfferModal = 'GuaranteedOfferModal',
    MinimumCreditLimit = 'MinimumCreditLimit',
    RepresentativeExample = 'RepresentativeExample',
    SortBy = 'SortBy',
}

interface ScoredTableProps {
    activeCategoryId: CreditCardProductCategory
    hasEverUsedNativeApp?: boolean
    highlightCard: HighlightCard
    minCreditLimitData?: GetMinimumGuaranteedCreditLimitResponse
    scoredCategories: ScoredCreditCardProductsCategory[]
    scoredExperiments?: ScoredExperiment[]
    thirdPartyReference?: string
}

interface ScoredTableState {
    numberOfCardsToShow: number
    selectedCategory: CreditCardProductCategory
    selectedSortKey: keyof CategorySortKeyComparatorKeys[CreditCardProductCategory]
}

interface ModalState {
    activeCard: ScoredCreditCardProduct | null
    activeModal: ModalType | null
    creditCardApplicationUrl: string | null
    totallySureTooltipBanner: boolean | null
}

const NUMBER_OF_CARDS_TO_SHOW = 15

export const defaultModalState: ModalState = {
    activeCard: null,
    activeModal: null,
    creditCardApplicationUrl: null,
    totallySureTooltipBanner: null,
}

const ScoredTable = ({
    activeCategoryId,
    hasEverUsedNativeApp,
    highlightCard,
    minCreditLimitData,
    scoredCategories,
    thirdPartyReference,
}: ScoredTableProps) => {
    const appConfig = getAppConfig()
    const logEvents = useLogEvents()
    const logEvents2 = useLogEvents2()
    const { trackAccept, trackAction, trackPageView, trackClickout } =
        useInscalarTrackEvent()

    const { appQueryState, getRouteWithAppState, updateAppQueryState } =
        useAppQueryState()
    const { query, isReady } = useRouter()

    const isNewSignUpJourney = query.isNewSignUp === 'true'

    const getCardApplicationUrl = useGetCreditCardApplicationUrl()
    const postCreditCardsEmailTrigger = usePostCreditCardsEmailTrigger()
    const logCheckMatchFactorRankingError = useLogCheckMatchFactorRankingError()

    const { data } = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const [modalState, updateModalState] =
        useSetState<ModalState>(defaultModalState)

    const [creditCardsState, setCreditCardsState] =
        useSetState<ScoredTableState>({
            selectedCategory: activeCategoryId,
            selectedSortKey: getValidCategorySortKey({
                categoryId: appQueryState.categoryId!,
                queryOption: appQueryState.sortBy as CategoryComparatorKey,
            }),

            numberOfCardsToShow:
                appQueryState.numberOfCardsToShow ?? NUMBER_OF_CARDS_TO_SHOW,
        })

    const newOfferProductCodes = useGetDifferenceInNewCreditCardsOffers(
        scoredCategories,
        creditCardsState.selectedCategory
    )

    const activeCategoryPath = getCategoryPathForId(
        creditCardsState.selectedCategory
    )

    const sponsoredCreditCards = useMemo(() => {
        return getSponsoredProducts(
            scoredCategories,
            creditCardsState.selectedCategory
        )
    }, [scoredCategories, creditCardsState.selectedCategory])

    const activeCategory = getCategoryByCategoryId(
        scoredCategories,
        creditCardsState.selectedCategory
    )

    const isHighlightCardEligible =
        highlightCard.status === HighlightCardStatus.CardEligible

    const downsellCreditCards = useMemo(() => {
        return getDownsellCreditCards(
            scoredCategories,
            creditCardsState.selectedCategory
        )
    }, [scoredCategories, creditCardsState.selectedCategory])

    const activeCategoryCreditCards = useMemo(() => {
        if (activeCategory == null) {
            return undefined
        }
        const sortedCards = sortCardsBySortKey({
            cards: activeCategory.creditCards,
            category: creditCardsState.selectedCategory,
            sortKey: getValidCategorySortKey({
                categoryId: creditCardsState.selectedCategory,
                queryOption: creditCardsState.selectedSortKey,
            }),
            categorySortKeyComparatorKeys,
        })
        return [
            ...(sponsoredCreditCards ?? []),
            ...sortedCards,
            ...(downsellCreditCards ?? []),
        ]
    }, [
        creditCardsState.selectedSortKey,
        creditCardsState.selectedCategory,
        activeCategory,
        downsellCreditCards,
        sponsoredCreditCards,
    ])

    const numberOfTotallySureCardOffers = useMemo(
        () =>
            activeCategoryCreditCards?.filter(
                card =>
                    getGuaranteedOfferType({ creditCard: card }) ===
                    GuaranteedOfferType.MarketingOffer
            ).length,
        [activeCategoryCreditCards]
    )

    const canShowTotallySureTooltipBanner =
        !!numberOfTotallySureCardOffers && numberOfTotallySureCardOffers > 0

    useEffect(() => {
        if (activeCategory && activeCategory.creditCards.length > 0) {
            const eventData = buildAcquisitionJourneyFormAcceptEventPayload(
                activeCategory.creditCards,
                isNewSignUpJourney
            )

            if (eventData) {
                logEvents.logAcquisitionJourneyCardsTableViewEvent(eventData)
            }
        }
    }, [isReady])

    useEffect(() => {
        logEvents.logScoredTableUpdateEvent({
            cards: activeCategoryCreditCards,
            minCreditLimit: minCreditLimitData?.minimumGuaranteedCreditLimit,
            category: creditCardsState.selectedCategory,
            sortedBy: creditCardsState.selectedSortKey,
            seenNewOffers: newOfferProductCodes.length > 0,
            numberOfNewOffersSeen: newOfferProductCodes.length,
        })

        if (sponsoredCreditCards?.length) {
            logEvents.logFeatureCardsViewEvent({
                category: creditCardsState.selectedCategory,
                numberOfFeaturedCards: sponsoredCreditCards.length,
            })
        }
    }, [activeCategoryCreditCards])

    const activeSortOptions = useMemo(() => {
        return getCategorySortKeysToShow(creditCardsState.selectedCategory)
    }, [creditCardsState.selectedCategory])

    const fireProductTableActionEvent = () => {
        trackAction({
            step: 'credit_cards_product_table',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
        })
    }

    const onCategoryChange = (category: CreditCardProductCategory) => {
        firstNewOfferItemRef.current = { element: null, productCode: null }
        logEvents.logCategoryChangeEvent({ category })

        setCreditCardsState({
            selectedCategory: category,
            selectedSortKey: getValidCategorySortKey({
                categoryId: category,
                queryOption: creditCardsState.selectedSortKey,
            }),
            numberOfCardsToShow: NUMBER_OF_CARDS_TO_SHOW,
        })

        fireProductTableActionEvent()
    }

    const onSortChange = (
        sortKey: keyof CategorySortKeyComparatorKeys[CreditCardProductCategory]
    ) => {
        logEvents.logSortChangeEvent({
            sortBy: sortKey,
            category: creditCardsState.selectedCategory,
        })

        setCreditCardsState({ selectedSortKey: sortKey })

        updateAppQueryState({
            sortby: sortKey,
        })

        fireProductTableActionEvent()
    }

    const resetModalState = () => {
        updateModalState(defaultModalState)
    }

    const onShowMoreResults = () => {
        const numberOfCardsToShow =
            creditCardsState.numberOfCardsToShow + NUMBER_OF_CARDS_TO_SHOW

        setCreditCardsState({ numberOfCardsToShow: numberOfCardsToShow })

        updateAppQueryState({ show: `${numberOfCardsToShow}` })

        trackPageView({
            step: 'credit_cards_product_table',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
            resultsSorting: creditCardsState.selectedSortKey,
            products: activeCategoryCreditCards!,
            highlightProduct:
                highlightCard.card && isHighlightCardEligible
                    ? highlightCard.card
                    : null,
            numberOfCardsToShow: numberOfCardsToShow,
        })
    }

    const onSeeMoreDetailsPress = ({
        card,
        cardPosition,
        trigger = ApplicationURLTriggerLocation.Row,
        isNewOffer = false,
    }: {
        card: ScoredCreditCardProduct
        cardPosition: number
        isNewOffer?: boolean
        trigger?: ApplicationURLTriggerLocation
    }) => {
        logEvents.logNavigateToCardPageEvent({
            productCode: card.productCode,
            rowNumber: cardPosition,
            trigger,
            isNewOffer,
        })
    }

    const onApplyPress = ({
        card,
        isBestMatchCard,
        cardPosition,
        applicationUrl,
        trigger = ApplicationURLTriggerLocation.Row,
        isNewOffer = false,
        clickId,
    }: {
        applicationUrl: string
        card: ScoredCreditCardProduct
        cardPosition: number
        clickId: string
        isBestMatchCard: boolean
        isNewOffer?: boolean
        trigger?: ApplicationURLTriggerLocation
    }) => {
        logEvents.logCardApplyEvent({
            category: creditCardsState.selectedCategory,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            isBestMatchCard: isBestMatchCard,
            isCounterOffer: card.guaranteedRate?.isCounterOffer ?? false,
            productCode: card.productCode,
            rowNumber: cardPosition,
            score: card.score,
            status: card.status,
            trigger,
            isNewOffer,
        })

        trackClickout({
            step: 'credit_cards_product_table',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
            product: card,
            productPosition: cardPosition - 1,
            allProducts: activeCategory?.creditCards ?? [],
        })

        logEvents2.logProductClickEvent({
            category: 'credit_cards',
            clickId,
            creditCard: card,
            productShownOrder: cardPosition,
            productRankOrder: card.categoryScore,
        })

        window.open(applicationUrl, '_blank')
    }

    const firstNewOfferItemRef = useRef<{
        element: HTMLLIElement | null
        productCode: string | null
    }>({ element: null, productCode: null })

    const hasFiredInitialMountRef = useRef(false)

    useEffect(() => {
        if (
            !isReady ||
            !activeCategory ||
            activeCategory.creditCards.length === 0 ||
            !activeCategoryCreditCards
        ) {
            return
        }

        if (!hasFiredInitialMountRef.current) {
            hasFiredInitialMountRef.current = true

            trackAccept({
                step: 'credit_cards_scoring',
                journey: 'credit_cards',
                journeyCategory: 'credit_cards',
            })

            logEvents2.logResultsScreenDisplayedEvent({
                displayedProducts: activeCategoryCreditCards,
                numberOfCardsToShow: creditCardsState.numberOfCardsToShow,
                category: 'credit_cards',
                highlightProduct:
                    highlightCard.card && isHighlightCardEligible
                        ? highlightCard.card
                        : null,
                maxCreditLimit: data?.minimumGuaranteedCreditLimit!,
            })
        }

        trackPageView({
            step: 'credit_cards_product_table',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
            resultsSorting: creditCardsState.selectedSortKey,
            products: activeCategoryCreditCards,
            highlightProduct:
                highlightCard.card && isHighlightCardEligible
                    ? highlightCard.card
                    : null,
            numberOfCardsToShow: creditCardsState.numberOfCardsToShow,
        })
    }, [activeCategoryCreditCards, isReady])

    const previousMinCreditLimitRef = useRef<number | null | undefined>(
        undefined
    )

    useEffect(() => {
        const previousMinCreditLimit = previousMinCreditLimitRef.current
        const currentMinCreditLimit = data?.minimumGuaranteedCreditLimit

        previousMinCreditLimitRef.current = currentMinCreditLimit

        // Skip the initial `undefined -> defined` settle from the query loading.
        // Only fire when the user has actively changed the credit limit.
        if (previousMinCreditLimit === undefined) return
        if (previousMinCreditLimit === currentMinCreditLimit) return
        if (!activeCategoryCreditCards) return

        trackPageView({
            step: 'credit_cards_product_table',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
            resultsSorting: creditCardsState.selectedSortKey,
            products: activeCategoryCreditCards,
            highlightProduct:
                highlightCard.card && isHighlightCardEligible
                    ? highlightCard.card
                    : null,
            numberOfCardsToShow: creditCardsState.numberOfCardsToShow,
        })
    }, [data?.minimumGuaranteedCreditLimit])

    const renderItem = (card: ScoredCreditCardProduct, index: number) => {
        const isDownsellCard = getCardIsDownsell(card)
        const isFirstDownsellCard = isDownsellCard && card.__isFirstDownsellCard
        const isSponsoredCard = getIsSponsoredCreditCard(card)
        const cardPosition = getCardPosition(index)

        const isBestMatch = getIsBestMatchCard({
            currentCategory: creditCardsState.selectedCategory,
            currentSortKey: creditCardsState.selectedSortKey,
            hasHighlightCard:
                highlightCard?.status === HighlightCardStatus.CardEligible,
            index,
        })
        const showNewOfferBadge = newOfferProductCodes.includes(
            card.productCode
        )

        const clickId = getClickId(card)

        const applicationUrl = getCardApplicationUrl({
            clickId,
            category: isDownsellCard
                ? 'PoorCredit'
                : creditCardsState.selectedCategory,
            creditCard: card,
            creditCardIndex: cardPosition,
            isBestMatchCard: isBestMatch,
            thirdPartyReference: thirdPartyReference!,
            trigger: ApplicationURLTriggerLocation.Row,
            isNewOffer: showNewOfferBadge,
        })

        const sanitisedCardName = getSanitisedCardName(card.name)
        const setRef = (el: HTMLLIElement) => {
            if (
                el &&
                showNewOfferBadge &&
                firstNewOfferItemRef.current.element == null
            ) {
                firstNewOfferItemRef.current.element = el
                firstNewOfferItemRef.current.productCode = card.productCode
            }
        }

        return (
            <ErrorBoundary
                fallback={null}
                onError={({ error }) => captureException(error)}
            >
                <Box as="li" ref={setRef}>
                    {isFirstDownsellCard ? (
                        <Box mb="space30">
                            <DownsellBanner />
                        </Box>
                    ) : null}

                    <Box
                        backgroundColor={isSponsoredCard ? 'indigo20' : 'none'}
                        pb={isSponsoredCard ? 'space30' : 0}
                    >
                        {isSponsoredCard ? (
                            <Flex py="space10" justifyContent="center">
                                <AdvertisementBanner />
                            </Flex>
                        ) : null}

                        <Grid>
                            <Box gridColumn="1/13">
                                <ScoredTableItem
                                    item={card}
                                    category={creditCardsState.selectedCategory}
                                    isBestMatch={isBestMatch}
                                    onShowGuaranteedOfferModal={() =>
                                        updateModalState({
                                            activeModal:
                                                ModalType.GuaranteedOfferModal,
                                            activeCard: card,
                                        })
                                    }
                                    onShowRepresentativeExampleModal={() =>
                                        updateModalState({
                                            activeModal:
                                                ModalType.RepresentativeExample,
                                        })
                                    }
                                    renderButtons={() => {
                                        return (
                                            <Flex
                                                justifyContent="center"
                                                flexDirection={[
                                                    'column',
                                                    'row-reverse',
                                                    'column',
                                                ]}
                                                gridGap="space15"
                                                width={['auto', 'auto', 212]}
                                            >
                                                <Box width="100%">
                                                    <LinkButton
                                                        text="Apply"
                                                        variant="primary"
                                                        href={applicationUrl}
                                                        rel="nofollow noopener"
                                                        target="_blank"
                                                        data-testid="applyButton"
                                                        disabled={!isReady}
                                                        isExternalLink={true}
                                                        data-gtm-event={
                                                            card.sponsored
                                                                ? 'applyNow-sponsored-row-postClick'
                                                                : 'applyNow-table-postClick'
                                                        }
                                                        onClick={event => {
                                                            event.preventDefault()

                                                            const acquisitionClickoutEventData =
                                                                buildAcquisitionJourneyScoredTableClickoutEventPayload(
                                                                    card,
                                                                    isNewSignUpJourney,
                                                                    activeCategoryCreditCards
                                                                )

                                                            if (
                                                                acquisitionClickoutEventData
                                                            ) {
                                                                logEvents.logAcquisitionJourneyCardsTableClickoutEvent(
                                                                    acquisitionClickoutEventData
                                                                )
                                                            }

                                                            onApplyPress({
                                                                card,
                                                                clickId,
                                                                cardPosition,
                                                                applicationUrl,
                                                                isBestMatchCard:
                                                                    isBestMatch,
                                                                isNewOffer:
                                                                    showNewOfferBadge,
                                                            })
                                                        }}
                                                    />
                                                </Box>
                                                <Box width="100%">
                                                    <NextLinkButton
                                                        text="More details"
                                                        variant="secondaryOutline"
                                                        to={{
                                                            ...getRouteWithAppState(
                                                                [
                                                                    isDownsellCard
                                                                        ? 'credit-cards-bad-credit'
                                                                        : activeCategoryPath,
                                                                    'card',
                                                                    sanitisedCardName,
                                                                ]
                                                            ),
                                                            query: {
                                                                ...getRouteWithAppState(
                                                                    [
                                                                        isDownsellCard
                                                                            ? 'credit-cards-bad-credit'
                                                                            : activeCategoryPath,
                                                                        'card',
                                                                        sanitisedCardName,
                                                                    ]
                                                                ).query,
                                                                cardPosition:
                                                                    cardPosition,
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            onSeeMoreDetailsPress(
                                                                {
                                                                    card,
                                                                    cardPosition,
                                                                    isNewOffer:
                                                                        showNewOfferBadge,
                                                                }
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            </Flex>
                                        )
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Box>
                </Box>
            </ErrorBoundary>
        )
    }

    const shouldShowAppPromotionHeader =
        typeof window !== 'undefined' &&
        window.innerWidth <= 425 &&
        appQueryState.showAppPromotion === 'true' &&
        hasEverUsedNativeApp

    const shouldShowAppPromotionModal =
        shouldShowAppPromotionHeader &&
        Cookies.get('appPromotionPreferWebToAppJourney') !== 'true'

    const highlightClickId = getClickId(highlightCard.card)

    useEffect(() => {
        if (shouldShowAppPromotionModal) {
            updateModalState({ activeModal: ModalType.AppAdoption })
            logEvents.logAppPromotionView({ promotion: 'creditCardsTable' })
        }
    }, [shouldShowAppPromotionModal])

    useEffect(() => {
        if (activeCategoryCreditCards == null) {
            return
        }
        postCreditCardsEmailTrigger(
            activeCategoryCreditCards.filter(
                card => !getIsSponsoredCreditCard(card)
            ),
            creditCardsState.selectedCategory
        )
    }, [
        activeCategoryCreditCards,
        creditCardsState.selectedCategory,
        postCreditCardsEmailTrigger,
    ])

    useEffect(() => {
        if (activeCategoryCreditCards == null) {
            return
        }

        if (creditCardsState.selectedSortKey === 'matchFactor') {
            logCheckMatchFactorRankingError(activeCategoryCreditCards)
        }
    }, [
        logCheckMatchFactorRankingError,
        activeCategoryCreditCards,
        creditCardsState.selectedCategory,
        creditCardsState.selectedSortKey,
    ])

    useEffect(() => {
        setCreditCardsState({ selectedCategory: activeCategoryId })
    }, [activeCategoryId])

    const totalCreditCards = scoredCategories[0].totalCreditCards

    const highlightCardApplicationUrl = isHighlightCardEligible
        ? getCardApplicationUrl({
              category: creditCardsState.selectedCategory,
              creditCard: highlightCard.card,
              creditCardIndex: 0,
              isBestMatchCard: false,
              thirdPartyReference: thirdPartyReference!,
              trigger: ApplicationURLTriggerLocation.Header,
              isNewOffer: false,
              clickId: highlightClickId,
          })
        : null

    const modalGuaranteedOfferType = getGuaranteedOfferType({
        creditCard: modalState.activeCard,
    })

    const handleNewOffersBannerClick = () => {
        if (firstNewOfferItemRef.current.element != null) {
            firstNewOfferItemRef.current?.element.scrollIntoView({
                behavior: 'smooth',
            })
            logEvents.logClickOnNewOffersBanner({
                productCode: firstNewOfferItemRef.current.productCode!,
            })
        }
    }

    return (
        <>
            {shouldShowAppPromotionHeader ? (
                <AppAdoptionHeader customerId={appQueryState.customerId} />
            ) : null}

            <Box pb={['space50', 'space50', 'space70']}>
                <Grid>
                    <Box mt="space30" mb="space10" gridColumn="1/13">
                        <LinkWithChevron
                            href={`${appConfig.ACCOUNT_URL}offers?customerId=${appQueryState.customerId}`}
                            variant="100"
                            direction="left"
                        >
                            Back to offers
                        </LinkWithChevron>
                    </Box>
                </Grid>
                <Grid>
                    <Box gridColumn="1/13" pb="space30">
                        <Box mb="space10">
                            <Heading variant="100">Your card results</Heading>
                        </Box>

                        <Box>
                            <Text variant="120">
                                {totalCreditCards}{' '}
                                {totalCreditCards === 1 ? 'offer' : 'offers'}{' '}
                                across all categories
                            </Text>
                        </Box>
                    </Box>

                    <Box gridColumn="1/13" mb="space20">
                        <BrokerInfo />
                    </Box>

                    {newOfferProductCodes.length > 0 ? (
                        <Box
                            gridColumn={['1/13', '1/13', '1/9']}
                            mt="space20"
                            style={{
                                cursor: firstNewOfferItemRef.current
                                    ? 'pointer'
                                    : 'default',
                            }}
                            role={
                                firstNewOfferItemRef.current
                                    ? 'button'
                                    : 'banner'
                            }
                            onClick={handleNewOffersBannerClick}
                        >
                            <ScoredTableNewOfferCard
                                numberOfNewOffers={newOfferProductCodes.length}
                            />
                        </Box>
                    ) : null}
                </Grid>

                {highlightCard.status !== HighlightCardStatus.NoData ? (
                    <Box backgroundColor={theme['indigo10']}>
                        <Grid>
                            <Box
                                gridColumn="1/13"
                                my="space30"
                                data-testid="highlight-hero"
                            >
                                <HighlightHero
                                    applicationUrl={highlightCardApplicationUrl}
                                    activeCategoryId={activeCategoryId}
                                    scoredCategories={scoredCategories}
                                    highlightedCreditCard={highlightCard}
                                    onApplyButtonPress={() => {
                                        if (isHighlightCardEligible) {
                                            onApplyPress({
                                                applicationUrl:
                                                    highlightCardApplicationUrl!,
                                                card: highlightCard.card,
                                                isBestMatchCard: false,
                                                cardPosition: 0,
                                                trigger:
                                                    ApplicationURLTriggerLocation.Header,
                                                clickId: highlightClickId,
                                            })
                                        }
                                    }}
                                    onSeeMoreDetailsPress={() => {
                                        if (isHighlightCardEligible) {
                                            onSeeMoreDetailsPress({
                                                card: highlightCard.card,
                                                cardPosition: 0,
                                                trigger:
                                                    ApplicationURLTriggerLocation.Header,
                                            })
                                        }
                                    }}
                                    onShowGuaranteedOfferModal={() =>
                                        updateModalState({
                                            activeModal:
                                                ModalType.GuaranteedOfferModal,
                                            activeCard:
                                                highlightCard.card as ScoredCreditCardProduct,
                                        })
                                    }
                                />
                            </Box>
                        </Grid>
                    </Box>
                ) : null}

                <Box mb="space20">
                    <ScoredTableHeader
                        isActivatedFilter={
                            creditCardsState.selectedCategory !== 'BestCards'
                        }
                        isActivatedSort={
                            creditCardsState.selectedSortKey !== 'matchFactor'
                        }
                        onShowCategoryFilterModal={() =>
                            updateModalState({
                                activeModal: ModalType.CategoryFilter,
                            })
                        }
                        onShowMinimumCreditLimitModal={() =>
                            updateModalState({
                                activeModal: ModalType.MinimumCreditLimit,
                            })
                        }
                        onShowSortByModal={() =>
                            updateModalState({ activeModal: ModalType.SortBy })
                        }
                        selectedSortKey={creditCardsState.selectedSortKey}
                    />
                </Box>

                {canShowTotallySureTooltipBanner ? (
                    <Grid>
                        <Box mb="space30" gridColumn="1/13">
                            <TotallySureTooltipBanner
                                numberOfTotallySureCardOffers={
                                    numberOfTotallySureCardOffers
                                }
                                onClick={() =>
                                    updateModalState({
                                        activeModal:
                                            ModalType.GuaranteedOfferModal,
                                        totallySureTooltipBanner: true,
                                    })
                                }
                            />
                        </Box>
                    </Grid>
                ) : null}

                {activeCategory &&
                creditCardsState.selectedCategory !== 'BestCards' ? (
                    <Grid>
                        <Box mb="space30" gridColumn="1/13">
                            <Text color="light" weight="bold">
                                {
                                    categories[
                                        creditCardsState.selectedCategory
                                    ].name
                                }{' '}
                                ({activeCategory.totalCreditCards}{' '}
                                {activeCategory.totalCreditCards === 1
                                    ? 'result'
                                    : 'results'}
                                )
                            </Text>
                        </Box>
                    </Grid>
                ) : null}

                <Box as="ul" display="grid" gridGap="space30" mb="space30">
                    {activeCategoryCreditCards
                        ?.slice(0, creditCardsState.numberOfCardsToShow)
                        .map((card, index) => {
                            return (
                                <Fragment
                                    key={`${card.productCode}_${
                                        getIsSponsoredCreditCard(card)
                                            ? 'sponsored'
                                            : ''
                                    }`}
                                >
                                    {renderItem(card, index)}
                                </Fragment>
                            )
                        })}
                </Box>
                <Grid>
                    {activeCategory != null ? (
                        <Box gridColumn="1/13" mb="space20">
                            <CardTablePagination
                                totalCreditCards={
                                    activeCategory.totalCreditCards
                                }
                                numberOfCardsToShow={
                                    creditCardsState.numberOfCardsToShow!
                                }
                                showMoreResultsButton={
                                    creditCardsState.numberOfCardsToShow <
                                    activeCategory.totalCreditCards
                                }
                                onShowMoreResults={onShowMoreResults}
                            />
                        </Box>
                    ) : null}

                    <Box gridColumn="1/13" mb="space20">
                        <BrokerInfo />
                    </Box>
                </Grid>
            </Box>

            <ScoredTableFaqs
                activeCategory={creditCardsState.selectedCategory}
            />

            <RepresentativeExampleModal
                showModal={
                    modalState.activeModal == ModalType.RepresentativeExample
                }
                onModalClose={resetModalState}
            />
            <AppAdoptionModal
                showModal={modalState.activeModal == ModalType.AppAdoption}
                onModalClose={resetModalState}
                customerId={appQueryState.customerId}
            />
            <MinimumCreditLimitModal
                showModal={
                    modalState.activeModal == ModalType.MinimumCreditLimit
                }
                onModalClose={resetModalState}
                onSubmit={fireProductTableActionEvent}
            />
            <GuaranteedOfferModal
                showModal={
                    modalState.activeModal === ModalType.GuaranteedOfferModal
                }
                onModalClose={resetModalState}
                offerType={
                    modalState.totallySureTooltipBanner
                        ? GuaranteedOfferType.MarketingOffer
                        : modalGuaranteedOfferType
                }
                showOfferDurationContent={
                    modalState.totallySureTooltipBanner
                        ? true
                        : getShowOfferDurationContent({
                              category: creditCardsState.selectedCategory,
                              card: modalState.activeCard,
                          })
                }
            />
            <SortByModal
                showModal={modalState.activeModal == ModalType.SortBy}
                onModalClose={resetModalState}
                initialActiveSortValue={creditCardsState.selectedSortKey}
                sortOptions={activeSortOptions}
                onSortChange={onSortChange}
            />
            <CategoryFilterModal
                showModal={modalState.activeModal == ModalType.CategoryFilter}
                onModalClose={resetModalState}
                categories={scoredCategories}
                initialActiveCategory={creditCardsState.selectedCategory}
                onCategoryChange={onCategoryChange}
            />
        </>
    )
}

export default ScoredTable
